import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import TrustScore from './TrustScore';
import { MapPin, Fuel, Calendar, Gauge, Heart, Wrench, Tag, Car, CheckCircle, Film } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ListingCard({ listing, showFavorite = true }) {
  const { user } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  
  const isPart = listing.listing_type === 'part';
  const isSold = listing.status === 'sold';

  // Get photo URL - prioritize external_photos (reliable) over local photos
  // Local uploads may not persist across deployments
  const getPhotoUrl = () => {
    // First try external_photos (Unsplash, etc.) - these are always reliable
    if (listing.external_photos?.length > 0) {
      return listing.external_photos[0];
    }
    // Then try photos (could be local or object store)
    if (listing.photos?.length > 0) {
      const photo = listing.photos[0];
      // Object store URLs or external URLs work directly
      if (photo.startsWith('http') || photo.startsWith('/api/files/')) {
        return photo.startsWith('http') ? photo : `${BACKEND_URL}${photo}`;
      }
      // Local uploads - prepend backend URL
      return `${BACKEND_URL}${photo}`;
    }
    // Default placeholder
    return isPart 
      ? 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop'
      : 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop';
  };
  
  const photo = getPhotoUrl();

  const formatPrice = (p) => new Intl.NumberFormat('en-GH').format(p);

  useEffect(() => {
    if (user && showFavorite && !isSold) {
      API.get(`/favorites/check/${listing.listing_id}`)
        .then(res => setIsFavorited(res.data.favorited))
        .catch(() => {});
    }
  }, [user, listing.listing_id, showFavorite, isSold]);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || favoriteLoading || isSold) return;
    
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await API.delete(`/favorites/${listing.listing_id}`);
        setIsFavorited(false);
      } else {
        await API.post(`/favorites/${listing.listing_id}`);
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('Favorite toggle failed:', err);
    }
    setFavoriteLoading(false);
  };

  return (
    <Link to={`/listing/${listing.listing_id}`} data-testid={`listing-card-${listing.listing_id}`}
      className={`group block bg-card border border-border/50 rounded-xl overflow-hidden hover-lift hover:border-gold/40 transition-colors ${isSold ? 'opacity-75' : ''}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img 
          src={photo} 
          alt={listing.title} 
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isSold ? 'grayscale' : ''}`}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = isPart 
              ? 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop'
              : 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop';
          }}
        />
        
        {/* SOLD Overlay */}
        {isSold && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="bg-red-500 text-white px-6 py-2 rounded-full font-bold text-lg flex items-center gap-2 transform -rotate-12 shadow-lg">
              <CheckCircle className="w-5 h-5" /> SOLD
            </div>
          </div>
        )}
        
        {!isSold && (
          <div className="absolute top-3 right-3">
            <TrustScore score={listing.trust_score || 0} size="sm" />
          </div>
        )}
        
        <div className="absolute top-3 left-3 flex gap-1.5">
          {/* Type Badge */}
          <Badge className={`border-none text-xs backdrop-blur-sm ${
            isPart ? 'bg-blue-500/80 text-white' : 'bg-black/60 text-white'
          }`}>
            {isPart ? <><Wrench className="w-3 h-3 mr-1" /> Part</> : <><Car className="w-3 h-3 mr-1" /> Car</>}
          </Badge>
          {listing.condition && !isSold && (
            <Badge className="bg-black/60 text-white border-none text-xs backdrop-blur-sm">
              {listing.condition}
            </Badge>
          )}
          {Array.isArray(listing.videos) && listing.videos.length > 0 && !isSold && (
            <Badge className="bg-amber-500/90 text-black border-none text-xs backdrop-blur-sm gap-1" data-testid={`video-badge-${listing.listing_id}`}>
              <Film className="w-3 h-3" /> Video
            </Badge>
          )}
        </div>
        
        {/* Favorite Button - hide for sold items */}
        {user && showFavorite && !isSold && (
          <button
            onClick={toggleFavorite}
            disabled={favoriteLoading}
            className={`absolute bottom-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
              isFavorited 
                ? 'bg-red-500 text-white' 
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
            data-testid={`favorite-btn-${listing.listing_id}`}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-sm leading-tight line-clamp-1">{listing.title}</h3>
        </div>
        <p className={`font-mono-display font-bold text-lg ${isSold ? 'text-muted-foreground line-through' : 'text-gold'}`}>
          GHS {formatPrice(listing.price)}
        </p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {isPart ? (
            <>
              {listing.part_category && <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {listing.part_category}</span>}
              {listing.compatible_makes?.length > 0 && (
                <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {listing.compatible_makes.slice(0, 2).join(', ')}{listing.compatible_makes.length > 2 ? '...' : ''}</span>
              )}
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {listing.location}</span>
            </>
          ) : (
            <>
              {listing.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {listing.year}</span>}
              <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> {listing.fuel_type || 'Petrol'}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {listing.location}</span>
              {listing.mileage && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {formatPrice(listing.mileage)} km</span>}
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
